import React, { useState, useEffect } from "react";
import { formValueSelector, reduxForm } from "redux-form";
import { connect, useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';
import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import { Alert } from "react-bootstrap";
import { authLogin, languageChange } from "./auth.actions";
import { getVerisoulSessionId } from '../../Utils/verisoulHelper';
import { LoadingSpinner } from "../../Layout/LoadingSpinner";
import "./Login.css";
import { Show } from "../../Layout";
import 'bootstrap/dist/css/bootstrap.min.css';
// ===== DISABLED 2026-05-04 — Sign in with OTP =====
// To re-enable: uncomment + see Item #2 implementation notes
// ===================================================
import { MobileLogin } from "./MobileLogin";
import Language from "../../Languages/Login/content.json"
import { Helmet } from "react-helmet";
import { ForgetPasswordEmailWithState } from "./ForgetPasswordEmailForm";
import GoogleReCaptcha from "./GoogleRecaptcha";
import { registerDict } from 'Languages/RegistrationTranslations';
import signupHeader from '../../assets/img/logoen-dxpanel.png';
import headerImage from '../../assets/img/sign-in-create-account.png';
import signUpImg from '../../assets/img/forwardarrow.png';
import hindiMainHeaderLogo from '../../assets/img/DXPanel-Hindi-Logo-without-BG.png';
import hindiLoginImg from '../../assets/img/hinditextnew.png';
import { branding, PORTAL_CODE } from '../../config/portalBranding';
import countryConfig from 'config/countryConfig';
import LanguageToggle from '../Common/LanguageToggle';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Invisible Google reCAPTCHA on login is India-only. Other portals (JP/UK/SG/PH/MY)
// keep the JSX unmounted so the badge can't collide with their layout and so
// non-IN sign-in stays exactly as it has been since the 2026-05-04 disable.
const IS_INDIA_PORTAL = PORTAL_CODE === 'IN';

// Per-portal header logo. Hindi locale keeps its Hindi-text asset. English/other
// locales use the country-specific branding logo, falling back to the generic
// DX Panel logo if the branding asset fails to load.
const englishHeaderLogo = branding.logoFront || signupHeader;

function Login(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [username, setUsername] = useState({ username: null });
  const [password, setPassword] = useState({ password: null });
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [referralId, setReferralId] = useState('');
  const [captcha, setCaptcha] = useState(true);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const history = useHistory();
  const { invalid, submitting } = props;
  const pageContents = props.language === 'hi'
    ? Language.LoginHindi
    : props.language === 'jp'
      ? Language.LoginJapanese
      : Language.LoginEnglish
  // Brand-placeholder resolver for the JP login panel strings (bug #5 pattern).
  const withBrand = (str) => (str || '').replace("{brand}", countryConfig.brandName)
  // The Japanese login layout is the only one that sources its left-panel and
  // heading copy from content.json; other portals keep the existing hardcoded/
  // branding-driven rendering unchanged.
  const isJapanese = props.language === 'jp'
  const lang = props.language ?? 'en';
  const [isErrorMsgTrue, setIsErrorMsgTrue] = useState(false);

  useEffect(() => {
    const url = window.location.href;
    let lang = url.split('=')[1];
    if (lang != undefined) {
      let urlLang = lang;
      setTimeout(() => {
        dispatch(languageChange(urlLang))
      }, 800);
    }
  }, [])

  useEffect(() => {
    const hash = window.location.hash;
    const regex = /referralId=([^&]*)/;
    const referralIdMatch = regex.exec(hash);
    const referralId = referralIdMatch ? referralIdMatch[1] : null;
    setReferralId(referralId)
  }, []);

  const onSubmit = async () => {
    const verisoulSessionId = await getVerisoulSessionId();

    props.authLogin(
      username.username,
      password.password,
      'password',
      props.language,
      history,
      verisoulSessionId
    );
    setIsErrorMsgTrue(true);
  }

  // Login-failure toasts are fired directly from authLogin's catch
  // (see auth.actions.js → showLoginErrorToast) — a reliable per-attempt
  // signal. They are intentionally NOT triggered from a state-diff effect
  // here, because a repeated identical error produces no authError change
  // for an effect to observe.


  const handleClick = () => {
    if (referralId) {
      const queryParams = {
        referralId: referralId,
      };
      const queryString = Object.keys(queryParams)
        .map(key => `${key}=${encodeURIComponent(queryParams[key])}`)
        .join('&');
      const loginUrl = `/auth/signup?${queryString}`;
      history.push(loginUrl)
    } else {
      history.push('/auth/signup')
    }
  };

  const handleCaptchaValidation = response => {
    setCaptcha(true);
    console.log(response)
  }

  let displayError;
  if (props.language === 'hi' && props.authError === 'User Not found.') {
    displayError = 'उपयोगकर्ता मौजूद नहीं।';
  } else if (props.language === 'hi' && props.authError === 'Invalid Password!') {
    displayError = 'गलत पासवर्ड।';
  } else if (props.authError === 'User Not found.') {
    displayError = 'User not exist';
  } else {
    displayError = props.authError;
  }

  const conditionalVarObj = {
    true: {
      "mainCardPadding": "p-3",
      "headerImgWidth": "30%",
      "allParagraph": '20px',
      "allVisibleBtn": "25px",
      "partition": "या",
      "inputPIcon": "fa fa-eye-slash",
      "newUserPadding": "0.3rem",
      "inputPType": "text",
    },
    false: {
      "mainCardPadding": "p-2",
      "headerImgWidth": "13%",
      "allParagraph": '16px',
      "allVisibleBtn": "16px",
      "partition": "OR",
      "inputPIcon": "fa fa-eye",
      "newUserPadding": "0.1rem",
      "inputPType": "password",
    },
  }

  return (
    <>
      <Helmet>
        <title>{`${branding.name} - Sign in`}</title>
      </Helmet>
      <section className="dxAuth-shell" style={{
        '--dx-primary': branding.primaryColor,
        '--dx-secondary': branding.secondaryColor || branding.primaryColor,
        '--dx-accent': branding.accentColor || branding.secondaryColor || branding.primaryColor,
        // Country-aware auth background image. Only the JP rule in Login.css
        // ([data-country="JP"]) actually paints this; other portals ignore it
        // and keep their solid-color gradient panel.
        '--dx-login-bg': branding.loginBg ? `url("${branding.loginBg}")` : 'none',
      }}>
        <aside className="dxAuth-leftPanel" data-country={branding.code}>
          <div className="dxAuth-leftPanel-glow" aria-hidden="true" />
          <div className="dxAuth-leftLogoWrap">
            <img
              src={lang == "hi" ? hindiMainHeaderLogo : englishHeaderLogo}
              alt={branding.name}
              className="dxAuth-leftLogo"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = signupHeader; }}
            />
          </div>
          <div className="dxAuth-leftContent">
            <div className="dxAuth-leftAccentTop" aria-hidden="true" />
            <h1 className="dxAuth-leftHeadline">
              <span className="dxAuth-leftHeadline-bold">
                {isJapanese ? pageContents.panel.headlineBold : 'Share your opinion.'}
              </span>
              <span className="dxAuth-leftHeadline-light">
                {isJapanese ? pageContents.panel.headlineLight : 'Earn rewards.'}
              </span>
            </h1>
            <p className="dxAuth-leftSubheadline">
              {isJapanese
                ? withBrand(pageContents.panel.subheadline)
                : (branding.tagline
                  ? `${branding.tagline} — join thousands of panelists shaping market research across ${branding.name}.`
                  : `Join thousands of panelists shaping market research across ${branding.name}.`)}
            </p>
          </div>
          <p className="dxAuth-leftFooter">
            {isJapanese
              ? withBrand(pageContents.panel.footer)
              : `© ${new Date().getFullYear()} ${branding.name} • Powered by DataXing`}
          </p>
        </aside>

        <main className="dxAuth-rightPanel">
          <div className="dxAuth-mobileLogoBar">
            <img
              src={lang == "hi" ? hindiMainHeaderLogo : englishHeaderLogo}
              alt={branding.name}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = signupHeader; }}
            />
          </div>
          <div className="dxAuth-formContainer">
            <h2 className="dxAuth-heading">
              {isJapanese ? pageContents.panel.welcomeHeading : 'Welcome back'}
            </h2>
            <p className="dxAuth-subheading">
              {isJapanese ? pageContents.panel.welcomeSubheading : 'Sign in to your account to continue.'}
            </p>

            <form
              onSubmit={(e) => { e.preventDefault(); props.handleSubmit(onSubmit)(e); }}
              className="dxAuth-form"
            >
              <div className="dxAuth-fieldGroup">
                <label htmlFor="dxAuth-loginEmail" className="dxAuth-label">{pageContents.items[4].title}</label>
                <input
                  id="dxAuth-loginEmail"
                  data-lang={lang}
                  maxLength={200}
                  type="email"
                  className="dxAuth-input"
                  name="email"
                  onChange={(e) => {
                    e.target.setCustomValidity("")
                    setUsername({ username: e.target.value })
                    setIsErrorMsgTrue(false);
                  }
                  }
                  required
                  title={registerDict[lang]['correct_email_format']}
                  pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                  onInput={(e) => e.target.setCustomValidity("")}
                  onInvalid={(e) => {
                    let msg = registerDict[lang]['correct_email_format'];
                    e.target.setCustomValidity(msg)
                  }}
                  placeholder={isJapanese ? pageContents.panel.emailPlaceholder : pageContents.items[4].title}
                  autoComplete="email"
                />
              </div>

              <div className="dxAuth-fieldGroup">
                <div className="dxAuth-labelRow">
                  <label htmlFor="dxAuth-loginPassword" className="dxAuth-label">{pageContents.items[5].title}</label>
                  <button
                    type="button"
                    className="dxAuth-forgotLink"
                    onClick={(e) => setIsPasswordReset(true)}
                  >
                    {pageContents.items[6].title}
                  </button>
                </div>
                <div className="dxAuth-passwordWrap">
                  <input
                    id="dxAuth-loginPassword"
                    data-lang={lang}
                    className="dxAuth-input"
                    name="pswd"
                    onChange={(e) => {
                      e.target.setCustomValidity("")
                      setPassword({ password: e.target.value })
                      setIsErrorMsgTrue(false);
                    }
                    }
                    title={registerDict[lang]['password_required']}
                    type={conditionalVarObj[isPasswordVisible]["inputPType"]}
                    onInput={(e) => e.target.setCustomValidity("")}
                    onInvalid={(e) => {
                      let msg = registerDict[lang]['validationMessage'] + ': ' + registerDict[lang]['password_required']
                      e.target.setCustomValidity(msg)
                    }}
                    placeholder={isJapanese ? pageContents.panel.passwordPlaceholder : pageContents.items[5].title}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    className={password.password ? "dxAuth-passwordToggle" : "dxAuth-passwordToggle dxAuth-passwordToggle--hidden"}
                    type="button"
                    aria-label="Toggle password visibility"
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  >
                    <i className={conditionalVarObj[!isPasswordVisible]["inputPIcon"]}></i>
                  </button>
                </div>
              </div>

              {IS_INDIA_PORTAL && (
                <GoogleReCaptcha onCaptchaValidation={handleCaptchaValidation} />
              )}

              <Alert
                variant="danger"
                show={props.authError && (props.authError == "User Not found." || props.authError === 'Invalid Password!') && isErrorMsgTrue}
                className="dxAuth-alert"
              >
                {displayError}
              </Alert>

              <div className="dxAuth-spinnerRow">
                <LoadingSpinner show={props.isLoading} />
                <Show when={true}>
                  {props?.error?.message ?? ''}
                </Show>
              </div>

              <button
                type="submit"
                className="dxAuth-primaryBtn"
                disabled={submitting || invalid || captcha === false}
              >
                <span className="dxAuth-primaryBtn-label">{pageContents.items[7].title}</span>
                {/* ===== DISABLED 2026-05-04 — Auth button arrow icon =====
                    Client requested removal. To re-enable: unwrap.
                    ====================================================== */}
                {/* <img src={signUpImg} alt="" className="dxAuth-primaryBtn-arrow" /> */}
              </button>

              {IS_INDIA_PORTAL && (
                <>
                  <div className="dxAuth-divider" role="separator">
                    <span>{t('auth.orSeparator')}</span>
                  </div>

                  <div className="dxAuth-secondaryBtns">
                    <button
                      type="button"
                      className="dxAuth-secondaryBtn dxAuth-secondaryBtn--otp"
                      onClick={() => setIsMobile(true)}
                    >
                      {pageContents.items[9].title}
                    </button>

                    {isMobile && <MobileLogin
                      referralId={referralId}
                      show={isMobile}
                      onClose={() => setIsMobile(false)}
                      onSubmit={() => {
                        setIsMobile(false)
                      }}
                    />}
                  </div>
                </>
              )}

              <p className="dxAuth-bottomLink">
                {pageContents.items[1].title}
                <Link to="#" onClick={handleClick} className="dxAuth-bottomLink-cta">
                  {pageContents.items[2].title}
                </Link>
              </p>

              {/* Pinned top-right via .dxAuth-langDropdown (position: fixed,
                  with a mobile `position: static` fallback in Login.css).
                  LanguageToggle renders nothing on en-only portals. */}
              <div className="dxAuth-langDropdown">
                <LanguageToggle />
              </div>
            </form>
          </div>
        </main>
      </section>
      <ForgetPasswordEmailWithState
        show={isPasswordReset}
        onHide={() => setIsPasswordReset(false)}
        onClose={() => setIsPasswordReset(false)}
      />
      <ToastContainer position="top-right" autoClose={5000} newestOnTop closeOnClick pauseOnHover />
    </>

  );
}

Login.propTypes = {
  authLogin: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  authError: PropTypes.string,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  language: PropTypes.string,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
};

const LoginWithRedux = reduxForm({ form: "loginForm" })(Login);

const selector = formValueSelector("loginForm");

const mapStateToProps = (state) => {
  const username = selector(state, "username") || false;
  return {
    isAuth: state.adminUser.adminUser.isAuthenticated,
    token: state.adminUser.adminUser.token,
    state: state,
    language: state.adminUser.adminUser.language,
    isLoading: state.adminUser.adminUser.loading,
    authError: state.adminUser.adminUser.error,
    username,
  };
};

const LoginWithState = connect(mapStateToProps, { authLogin })(LoginWithRedux);

export { Login, LoginWithState };
